import React from 'react';
import PropTypes from 'prop-types';
import update from 'immutability-helper';
import TextareaAutosize from 'react-autosize-textarea';
import { Modal } from 'react-bootstrap';
import { putRequest } from '../../../requestUtils';
import LocationAutocomplete from '../../common/LocationAutocomplete';
import ProjectLoadingIndicator from '../ProjectLoadingIndicator';
import './ProjectBudgetLogistics.scss';
import FilterTag from '../../common/FilterTag';
import MultipleButtonSelect from '../../common/MultipleButtonSelect';
import SearchIcon from '../../../../assets/images/search-icon.svg';

const travelChoices = {
  notApplicable: 'n/a',
  notSure: 'not sure',
  travel: 'travel',
};

const officeChoices = {
  notSure: 'not sure',
  remote: 'remote',
  office: 'office',
  hybrid: 'hybrid',
};

const officeOptions = [{ title: 'Remote', value: 'remote' },
  { title: 'In-Office', value: 'office' },
  { title: 'Hybrid', value: 'hybrid' },
  { title: 'Do not know yet', value: 'not sure' }];

const authorizationOptions = [
  { title: 'Yes', value: true },
  { title: 'No', value: false },
];

const backgroundChecksOptions = [
  'Background checks',
  'Drug Screen',
  'Covid Vaccine Proof',
];

const getDefaultOfficeData = (officeData) => {
  const result = {
    choice: officeData.work_location,
    days: '' || 5,
    required: false,
    location: '',
  };

  if (result.choice === officeChoices.office || result.choice === officeChoices.hybrid) {
    result.days = officeData.days_in_office || 5;
    result.required = officeData.office_location_required;
    result.location = officeData.office_location;
  }

  return result;
};

const getDefaultTravelData = (travelData) => {
  const result = {
    choice: travelData.travel_time || travelChoices.notApplicable,
    days: '',
    locations: [{ id: null, description: '' }],
    required: false,
  };

  if (result.choice === travelChoices.travel) {
    result.days = travelData.days_of_travel;
    result.required = travelData.travel_location_required;
    result.locations = travelData.travel_locations;
  }

  return result;
};

class ProjectBudgetLogistics extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      loading: true,
      showModal: false,
      office: {
        choice: '',
        days: '' || 5,
        location: '',
        required: false,
      },
      travel: {
        choice: '',
        days: '',
        locations: [],
        required: false,
      },
      deletedTravelLocationIds: [],
      selectedProjectLength: null, // option object with value/label
      selectedHoursPerWeek: null, // option object with value/label
      projectLengthPicklist: [],
      hoursPerWeekPicklist: [],
      budgetMinimum: '',
      budgetMaximum: '',
      basePayHidden: false,
      workAuthorizationRequired: null,
      compensationBenefitNote: '',
      backgroundCheckType: [],
    };

    this.handleBack = this.handleBack.bind(this);
    this.handleSubmit = this.handleSubmit.bind(this);
    this.setAttribute = this.setAttribute.bind(this);
    this.handleOnKeyDown = this.handleOnKeyDown.bind(this);
    this.renderBackgroundChecks = this.renderBackgroundChecks.bind(this);
    this.addBlankTravelLocation = this.addBlankTravelLocation.bind(this);
    this.handleWorkplaceTypeChange = this.handleWorkplaceTypeChange.bind(this);
    this.handleBackgroundCheckSelect = this.handleBackgroundCheckSelect.bind(this);
    this.renderCompensationAndBenefits = this.renderCompensationAndBenefits.bind(this);

    this.travelLocationsKey = 0;
  }

  componentDidMount() {
    const { getProjectDetails } = this.props;

    getProjectDetails('logistics')
      .then(({ data }) => {
        this.setInitialState(data);
      });
  }

  setAttribute(object, field, value) {
    const oldObj = this.state[object];
    const newObj = update(oldObj, {
      [field]: { $set: value },
    });

    this.setState({ [object]: newObj });
  }

  getTravelLocationParams(deleteLocations = false) {
    const { travel, deletedTravelLocationIds } = this.state;
    const result = {};
    let filteredLocations;
    if (deleteLocations) {
      filteredLocations = travel.locations.filter(
        (location) => location.id,
      ).map(
        (location) => ({ id: location.id, _destroy: 1 }),
      );
    } else {
      filteredLocations = travel.locations.filter((location) => location.description);
    }
    const deletedTravelLocations = deletedTravelLocationIds.map((id) => ({ id, _destroy: 1 }));
    const locationsArr = filteredLocations.concat(deletedTravelLocations);

    locationsArr.forEach((location, index) => {
      result[index] = location;
    });
    return result;
  }

  setTravelLocation(index, description) {
    const { travel } = this.state;
    const newTravel = update(travel, {
      choice: { $set: (description === '') ? 'n/a' : 'travel' },
      locations: {
        [index]: {
          description: { $set: description },
        },
      },
    });

    this.setState({ travel: newTravel });
  }

  // data from getProjectDetails() request
  setInitialState(data) {
    const projectLengthPicklist = data.duration_picklist;
    const hoursPerWeekPicklist = data.hours_picklist;

    const defaultOfficeData = getDefaultOfficeData(data);
    const defaultTravelData = getDefaultTravelData(data);

    this.setState({
      loading: false,
      budgetMinimum: String(data.budget_minimum || '').toLocaleString(),
      budgetMaximum: String(data.budget_maximum || '').toLocaleString(),
      rateNegotiable: data.rate_negotiable,
      remoteLocation: data.remote_preferred_location,
      projectLengthPicklist,
      hoursPerWeekPicklist,
      office: defaultOfficeData,
      travel: defaultTravelData,
      deletedTravelLocationIds: [],
      selectedProjectLength: {
        value: data.duration_range,
        label: data.duration_range,
      },
      selectedHoursPerWeek: {
        value: data.avg_time_required_range,
        label: data.avg_time_required_range,
      },
      basePayHidden: data.base_pay_hidden,
      employmentType: data.employment_type,
      workAuthorizationRequired: data.us_work_authorization_required,
      remotePreferredLocationNotApplicable: data.remote_preferred_location_not_applicable,
      travelLocationNotApplicable: data.travel_location_not_applicable,
      backgroundCheckType: data.background_check_type,
      compensationBenefitNote: data.compensation_benefit_note,
    });
  }

  addTravelLocationAttrs(attrs) {
    const { travel } = this.state;

    // populate attributes only if travel is chosen
    if (travel.choice === travelChoices.travel) {
      const travelLocations = this.getTravelLocationParams();

      Object.assign(attrs, {
        days_of_travel: travel.days,
        travel_location_required: travel.required,
        project_travel_locations_attributes: travelLocations,
      });
    } else {
      // if travel is not chosen, destroy any chosen travel locations
      const travelLocations = this.getTravelLocationParams(true);

      Object.assign(attrs, {
        project_travel_locations_attributes: travelLocations,
      });
    }
  }

  addOfficeLocationAttrs(attrs) {
    const { office, remoteLocation } = this.state;

    // populate attributes only if office work is chosen
    if (office.choice === officeChoices.office || office.choice === officeChoices.hybrid) {
      Object.assign(attrs, {
        office_location: office.location,
        office_days: office.days,
        days_in_office: office.days,
        office_location_required: office.required,
      });
    } else if (office.choice === officeChoices.remote) {
      Object.assign(attrs, {
        office_location: office.location,
        remote_preferred_location: remoteLocation,
      });
    }
  }

  removeTravelLocation(index) {
    const { travel } = this.state;
    const deletedLocation = this.state.travel.locations[index];
    const newTravel = update(travel, {
      locations: { $splice: [[index, 1]] },
    });

    if (deletedLocation.id) {
      const newDeletedLocationIds = update(this.state.deletedTravelLocationIds, {
        $push: [deletedLocation.id],
      });

      this.setState({ deletedTravelLocationIds: newDeletedLocationIds });
    }

    this.setState({ travel: newTravel });
  }

  ableToSubmit() {
    const {
      office, travel, budgetMinimum, budgetMaximum, backgroundCheckType,
    } = this.state;
    let travelValid = true;
    let officeValid = office.choice;

    const budgetLow = Number(String(budgetMinimum).replace(/,/, ''));
    const budgetHigh = Number(String(budgetMaximum).replace(/,/, ''));
    const budgetValid = budgetLow >= 1 && budgetHigh >= 1 && budgetHigh > budgetLow;

    if (travel.choice && travel.choice === travelChoices.travel) {
      travelValid = travel.locations.filter((location) => location.description).length > 0;
    }

    if (office.choice === officeChoices.office || office.choice === officeChoices.hybrid) {
      const parsedDays = parseInt(office.days, 10);
      officeValid = office.days
                      && office.location
                      && parsedDays >= 0
                      && parsedDays <= 7;
    }

    return budgetValid && officeValid && travelValid && backgroundCheckType;
  }

  addBlankTravelLocation() {
    const { travel } = this.state;

    this.travelLocationsKey -= 1;
    const newTravel = update(travel, {
      locations: {
        $push: [{
          id: null, description: '', _destroy: false, key: this.travelLocationsKey,
        }],
      },
    });

    this.setState({ travel: newTravel });
  }

  handleBack() {
    if (this.props.formType === 'page') {
      this.props.back();
    } else {
      this.setState({ showModal: false });
    }
  }

  handleOnKeyDown(event) {
    if (event.keyCode === 32) {
      event.stopPropagation();
    }
  }

  handleSubmit() {
    const { projectResourceId, csrfToken, afterSubmit } = this.props;
    const {
      office,
      travel,
      budgetMinimum,
      budgetMaximum,
      rateNegotiable,
      basePayHidden,
      workAuthorizationRequired,
      remotePreferredLocationNotApplicable,
      travelLocationNotApplicable,
      backgroundCheckType,
      compensationBenefitNote,
    } = this.state;

    const projectAttributes = {
      budget_minimum: String(budgetMinimum).replace(/,/, ''),
      budget_maximum: String(budgetMaximum).replace(/,/, ''),
      rate_negotiable: rateNegotiable,
      travel_time: travel.choice,
      days_of_travel: null,
      travel_location_required: false,
      work_location: office.choice,
      office_location: null,
      office_days: null,
      days_in_office: null,
      office_location_required: false,
      remote_preferred_location: null,
      base_pay_hidden: basePayHidden,
      us_work_authorization_required: workAuthorizationRequired,
      remote_preferred_location_not_applicable: remotePreferredLocationNotApplicable,
      travel_location_not_applicable: travelLocationNotApplicable,
      background_check_type: backgroundCheckType,
      compensation_benefit_note: compensationBenefitNote,
    };

    this.addOfficeLocationAttrs(projectAttributes);
    this.addTravelLocationAttrs(projectAttributes);

    putRequest(
      `/projects/${projectResourceId}`,
      { project: projectAttributes },
      csrfToken,
    ).then(() => afterSubmit());
  }

  handleWorkplaceTypeChange(choice) {
    this.setAttribute('office', 'choice', choice);
  }

  handleBackgroundCheckSelect(value) {
    const { backgroundCheckType } = this.state;
    if (backgroundCheckType.includes(value)) {
      backgroundCheckType.pop(value);
    } else {
      backgroundCheckType.push(value);
    }

    this.setState({ backgroundCheckType });
  }

  renderOfficeLocationInput() {
    const { office } = this.state;
    return (
      <div className="location-input">
        <LocationAutocomplete
          inputId="office-location-input"
          value={office.location}
          onValueChange={(newLocation) => this.setAttribute('office', 'location', newLocation)}
          defaultSample="Ex: 123 Boston Way, Boston, MA"
        />
        <img src={SearchIcon} className="search-icon" alt="search-icon" />
      </div>
    );
  }

  renderRemoteLocationInput() {
    const { remotePreferredLocationNotApplicable } = this.state;

    const { remoteLocation } = this.state;
    return (
      <div className="remote-preferred-location mb-3">
        <div className="g-row">
          <input
            className="remote-location-input"
            type="text"
            id="remote-location-input"
            name="remote-location-input"
            value={remoteLocation || ''}
            onChange={(newLocation) => this.setState({ remoteLocation: newLocation.target.value })}
            placeholder={remotePreferredLocationNotApplicable ? 'N/A'
              : 'Ex: Bay area, CA; Conn, NJ, or NY'}
            disabled={remotePreferredLocationNotApplicable}
          />
        </div>
        <div className="g-row remote-location-not-applicable mt-2">
          <label
            htmlFor="remote-preferred-location-not-applicable"
            className="custom-checkbox-container"
          >
            <input
              type="checkbox"
              id="remote-preferred-location-not-applicable"
              name="remote-preferred-location-not-applicable"
              className="checkbox-box"
              checked={remotePreferredLocationNotApplicable}
              onChange={(e) => this.setState({
                remotePreferredLocationNotApplicable: e.target.checked,
                remoteLocation: '',
              })}
            />
            <span className="checkmark" />
            <span className={`checkbox-text ${remotePreferredLocationNotApplicable ? 'active' : ''}`}>
              Not Applicable
            </span>
          </label>
        </div>
      </div>
    );
  }

  renderWorkAuthorization() {
    const { employmentType, workAuthorizationRequired } = this.state;

    if (employmentType === 'full_time') {
      return (
        <div className="work-authorization-container">
          <div className="logistics-header">
            Do you require candidates to have a work authorization for the United States?
            <span className="required-text">* Required</span>
          </div>
          <FilterTag
            options={authorizationOptions}
            handleChange={(newState) => this.setState({ workAuthorizationRequired: newState })}
            selected={workAuthorizationRequired}
          />
        </div>
      );
    }
    return null;
  }

  renderWorkplaceChoice() {
    return (
      <div className="workplace-type-container">
        <div className="logistics-header">
          Workplace Type
          <span className="required-text">* Required</span>
        </div>
        <FilterTag
          options={officeOptions}
          handleChange={this.handleWorkplaceTypeChange}
          selected={this.state.office.choice}
        />
      </div>
    );
  }

  renderOfficeQuestion() {
    const officeType = this.state.office.choice;
    return (
      <div className="workplace-questions-container">
        {(officeType === 'office' || officeType === 'hybrid')
          && (
          <div>
            <div className="logistics-header">
              Days in the Office
              <span className="required-text">* Required</span>
            </div>
            <div className="office-days-container">
              <input
                className="office-days-input"
                placeholder="0"
                type="number"
                id="office-days"
                name="office-days"
                onChange={(e) => this.setAttribute('office', 'days', e.target.value)}
                value={this.state.office.days}
              />
              <span className="office-days-text">
                days per week in the office.
              </span>
            </div>
            <div className="logistics-header">
              Office Location
              <span className="required-text">* Required</span>
            </div>
            {this.renderOfficeLocationInput()}
          </div>
          )}
        {officeType === 'remote'
          && (
          <div>
            <div className="logistics-header">
              Desired Candidate Location
            </div>
            {this.renderRemoteLocationInput()}
          </div>
          )}
      </div>
    );
  }

  renderTravelQuestion() {
    const { travelLocationNotApplicable } = this.state;

    const travelLocation = this.state.travel.locations[0].description;
    return (
      <div>
        <div className="logistics-header">
          Travel Requirements
        </div>
        <div className="travel-location mt-2">
          <input
            className="travel-location-input"
            type="text"
            id="travel-location-input"
            name="travel-location-input"
            disabled={travelLocationNotApplicable}
            value={travelLocation || ''}
            onChange={(newLocation) => this.setTravelLocation(0, newLocation.target.value)}
            placeholder={travelLocationNotApplicable
              ? 'N/A' : 'Ex: 40% travel to clinical sites in the East Coast'}
          />
        </div>
        <div className="g-row travel-not-applicable mt-2">
          <label
            htmlFor="travel-not-applicable"
            className="custom-checkbox-container"
          >
            <input
              type="checkbox"
              id="travel-not-applicable"
              name="travel-not-applicable"
              className="checkbox-box"
              checked={travelLocationNotApplicable}
              onChange={(e) => {
                this.setTravelLocation(0, '');
                this.setState({ travelLocationNotApplicable: e.target.checked });
              }}
            />
            <span className="checkmark" />
            <span className={`checkbox-text ${travelLocationNotApplicable ? 'active' : ''}`}>
              Not Applicable
            </span>
          </label>
        </div>
      </div>
    );
  }

  renderHourlyRate() {
    const {
      rateNegotiable, budgetMinimum, budgetMaximum, basePayHidden, employmentType,
    } = this.state;

    const salaryFormatter = (employmentType === 'full_time'
      ? (e) => Number(e.target.value.replace(/,/, '')).toLocaleString()
      : (e) => Number(e.target.value)
    );

    return (
      <div className="body-text-2 normal-weight">
        <div className="logistics-header">
          { employmentType === 'full_time'
            ? 'Annual Base Salary Range'
            : 'Preferred Hourly Rate'}
          <span className="required-text">* Required</span>
        </div>
        <div className="budget-container">
          <div className="budget-input-container">
            <span className="dollar-icon">$</span>
            <input
              name="budget-minimum"
              id="budget-minimum"
              className="form-control rate-form budget-input"
              placeholder="Minimum"
              required="required"
              value={budgetMinimum || ''}
              onChange={(e) => this.setState({ budgetMinimum: salaryFormatter(e) })}
            />
          </div>
          <span className="mx-3 range-span">-</span>
          <div className="budget-input-container">
            <span className="dollar-icon">$</span>
            <input
              name="budget-maximum"
              id="budget-maximum"
              className="form-control rate-form budget-input"
              placeholder="Maximum"
              required="required"
              value={budgetMaximum || ''}
              onChange={(e) => this.setState({ budgetMaximum: salaryFormatter(e) })}
            />
          </div>
        </div>
        <div className="rate-question">
          <label
            htmlFor={employmentType === 'full_time'
              ? 'base-pay-hidden-checkbox' : 'rate-negotiable-checkbox'}
            className="custom-checkbox-container"
          >
            { employmentType === 'full_time'
              ? (
                <input
                  type="checkbox"
                  id="base-pay-hidden-checkbox"
                  name="base-pay-hidden-checkbox"
                  className="checkbox-box"
                  checked={basePayHidden}
                  onChange={(e) => this.setState({ basePayHidden: e.target.checked })}
                />
              )
              : (
                <input
                  type="checkbox"
                  id="rate-negotiable-checkbox"
                  name="rate-negotiable-checkbox"
                  className="checkbox-box"
                  checked={rateNegotiable}
                  onChange={(e) => this.setState({ rateNegotiable: e.target.checked })}
                />
              )}
            <span className="checkmark" />
            { employmentType === 'full_time'
              ? (
                <span className={`checkbox-text ${basePayHidden ? 'active' : ''}`}>
                  Hide this from applicants
                </span>
              )
              : (
                <span className={`checkbox-text ${rateNegotiable ? 'active' : ''}`}>
                  Rate is negotiable
                </span>
              )}
          </label>
        </div>
      </div>
    );
  }

  renderCompensationAndBenefits() {
    const { employmentType, compensationBenefitNote } = this.state;

    return (
      <div className="body-text-2 normal-weight">
        {employmentType === 'full_time'
          && (
          <div className="compensation-container">
            <div className="logistics-header">Notes on Compensation & Benefits</div>
            <TextareaAutosize
              className="compensation-note"
              placeholder="Enter text here "
              onChange={(event) => this.setState({ compensationBenefitNote: event.target.value })}
              onKeyDown={(event) => this.handleOnKeyDown(event)}
              value={compensationBenefitNote}
            />
          </div>
          )}
      </div>
    );
  }

  renderBackgroundChecks() {
    return (
      <div className="background-check-container">
        <div className="logistics-header">
          Do you require candidates to undergo a drug screen,
          provide proof of COVID vaccination, or pass a background check?
          <span className="required-text">* Required</span>
        </div>
        <MultipleButtonSelect
          options={backgroundChecksOptions}
          handleChange={(value) => this.handleBackgroundCheckSelect(value)}
          selected={this.state.backgroundCheckType}
        />
      </div>
    );
  }

  renderButtons() {
    const { backButtonText, submitButtonText } = this.props;
    const nextButtonDisabled = !this.ableToSubmit();

    return (
      <div className="footer-buttons-container mt-4">
        <button className="btn btn-secondary button-back" onClick={this.handleBack}>
          {backButtonText}
        </button>
        <button
          className="btn btn-primary button-submit"
          onClick={this.handleSubmit}
          disabled={nextButtonDisabled}
        >
          {submitButtonText}
        </button>
      </div>
    );
  }

  renderContent() {
    const { showBackgroundCheck } = this.props;
    if (this.state.loading) {
      return (
        <ProjectLoadingIndicator />
      );
    }

    const pageView = this.props.formType === 'page';

    return (
      <div className="style-guide project-logistics-container">
        <div className="g-row">
          {pageView && <div className="g-column g-flex-1" />}
          <div className={`g-column ${pageView ? 'g-flex-6' : ''} logistics-entry-container-feature`}>
            <p className="budget-header-text">Compensations</p>
            {this.renderHourlyRate()}
            {showBackgroundCheck && this.renderCompensationAndBenefits()}
            <p className="logistics-header-text mt-3">Logistics</p>
            {this.renderWorkAuthorization()}
            {showBackgroundCheck && this.renderBackgroundChecks()}
            {this.renderWorkplaceChoice()}
            {this.renderOfficeQuestion()}
            {this.renderTravelQuestion()}
            {this.renderButtons()}
          </div>
        </div>
      </div>
    );
  }

  renderModal() {
    return (
      <Modal
        show={this.state.showModal}
        onHide={() => this.setState({ showModal: false })}
        dialogClassName="project-creation-modal project-logistics-modal"
      >
        <Modal.Header closeButton>
          <div className="style-guide project-creation-modal-heading">
            <h1>Edit Logistics</h1>
          </div>
        </Modal.Header>
        <Modal.Body className="project-creation-modal-body project-logistics-modal-body">
          {this.renderContent()}
        </Modal.Body>
      </Modal>
    );
  }

  renderModalLink() {
    return (
      <i
        role="button"
        tabIndex={0}
        className="fa fa-edit modal-link"
        onClick={() => this.setState({ showModal: true })}
      >
        {(this.props.formType === 'modal-link') ? 'Edit' : ''}
      </i>
    );
  }

  render() {
    if (this.props.formType === 'page') {
      return this.renderContent();
    }
    return (
      <div>
        { this.renderModalLink() }
        { this.renderModal() }
      </div>
    );
  }
}

ProjectBudgetLogistics.propTypes = {
  formType: PropTypes.string,
  csrfToken: PropTypes.string.isRequired,
  projectResourceId: PropTypes.string,
  back: PropTypes.func,
  afterSubmit: PropTypes.func,
  getProjectDetails: PropTypes.func.isRequired,
  backButtonText: PropTypes.string,
  submitButtonText: PropTypes.string,
  showBackgroundCheck: PropTypes.bool,
};

ProjectBudgetLogistics.defaultProps = {
  formType: 'page',
  projectResourceId: null,
  back: () => {},
  afterSubmit: () => {},
  backButtonText: 'Back',
  submitButtonText: 'Next',
  showBackgroundCheck: false,
};

export default ProjectBudgetLogistics;
